import React, { useEffect, useState } from 'react';
import {
    Card, CardBody, Col, Container, Row, Form, FormGroup, Label, Input,
    Button, Alert, Spinner, Badge
} from 'reactstrap';
import { toast } from 'react-toastify';
import api from '../../api';
import { assetUrl } from '../../utils/assetBase'; // HR Round 5 / Issue 8

// ─────────────────────────────────────────────────────────────────────────────
// Phase 3 / Feature 3 — Employee Self-Service "My Profile" page.
//
// Shows current contact info from EmpBasic. Employee edits and submits;
// changes go into ProfileUpdateRequests with Status='Pending' and HR
// reviews from /hr/profile-approvals.
//
// One pending request at a time. If a pending request exists, the form is
// read-only and a banner shows the proposed changes with a "Withdraw"
// option.
// ─────────────────────────────────────────────────────────────────────────────
const MyProfile = () => {
    const [data, setData]         = useState(null);
    const [form, setForm]         = useState({});
    const [saving, setSaving]     = useState(false);
    const [loading, setLoading]   = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('profile/me');
            setData(r.data);
            setForm(r.data?.current || {});
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Failed to load profile.');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const onChange = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

    // HR Round 6 / Issue 10 — advisory ZIP → City/State suggestion (same
    // /api/address/suggest the HR employee form uses; provider-driven,
    // never blocking).
    // HR Round 8 — DMV-style street autocomplete (shared /api/address/
    // autocomplete endpoint; disabled providers make this a silent no-op).
    const [acItems, setAcItems] = useState([]);
    const [acOpen, setAcOpen] = useState(false);
    const acTimer = React.useRef(null);
    const acSkipNext = React.useRef(false);

    const acSearch = (text) => {
        if (acTimer.current) clearTimeout(acTimer.current);
        if (acSkipNext.current) { acSkipNext.current = false; return; }
        const q = String(text || '').trim();
        if (q.length < 4) { setAcItems([]); setAcOpen(false); return; }
        acTimer.current = setTimeout(async () => {
            try {
                const r = await api.get('address/autocomplete', { params: { q } });
                if (r.data?.enabled && Array.isArray(r.data.items) && r.data.items.length) {
                    setAcItems(r.data.items); setAcOpen(true);
                } else { setAcItems([]); setAcOpen(false); }
            } catch { setAcItems([]); setAcOpen(false); }
        }, 300);
    };

    const acPick = async (item) => {
        setAcOpen(false);
        let it = item;
        if (item.placeId && !item.street) {
            try { it = (await api.get('address/expand', { params: { placeId: item.placeId } })).data || item; }
            catch { it = item; }
        }
        acSkipNext.current = true;
        setForm(prev => ({
            ...prev,
            address:  it.street || (it.label ? it.label.split(',')[0] : prev.address),
            address2: it.street2 || prev.address2,
            city:     it.city  || prev.city,
            state:    it.state || prev.state,
            zip:      it.zip   || prev.zip,
        }));
    };

    const onZipBlur = async () => {
        const digits = String(form.zip || '').replace(/\D/g, '');
        if (digits.length < 5 || !!data?.pendingRequest) return;
        try {
            const r = await api.get('address/suggest', { params: { zip: digits.slice(0, 5) } });
            const top = r.data?.suggestions?.[0];
            if (!top) return;
            setForm(prev => ({
                ...prev,
                city:  prev.city  || top.city,
                state: prev.state || top.state,
            }));
        } catch { /* advisory only */ }
    };

    const submit = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            await api.post('profile/me/update', payload);
            toast.success('Submitted for HR review.');
            await load();
        } catch (e) {
            const msg = e?.response?.data?.message || 'Submission failed.';
            toast.error(msg);
        } finally { setSaving(false); }
    };

    const withdraw = async () => {
        if (!data?.pendingRequest?.id) return;
        if (!window.confirm('Withdraw your pending profile update?')) return;
        try {
            await api.delete(`profile/me/update/${data.pendingRequest.id}`);
            toast.success('Withdrawn.');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Withdraw failed.');
        }
    };

    if (loading && !data) {
        return (
            // Round GEN-3 — wrapped in .page-content: this page was rendering a
            // bare <Container>, so it slid UNDER the fixed topbar (the photo
            // header was hidden). .page-content is the standard layout wrapper
            // every other page uses; it supplies the topbar clearance padding.
            <div className="page-content">
                <Container fluid className="text-center py-5">
                    <Spinner /><p className="text-muted mt-2 mb-0">Loading…</p>
                </Container>
            </div>
        );
    }
    if (!data) return null;

    const hasPending = !!data.pendingRequest;
    const proposed   = data.pendingRequest?.proposedChanges || {};

    return (
        // Round GEN-3 — .page-content wrapper (see note above): clears the
        // fixed topbar so the profile header is fully visible.
        <div className="page-content">
        <Container fluid>
            <Row className="mb-3 align-items-center">
                {/* HR Round 5 / Issue 8 — consistent avatar: the SAME image the
                    top-bar shows (authUser.imagePath via assetUrl), so the profile
                    page and the header can never disagree. Photo changes are an
                    HR action (employee grid → edit → Change photo), so employees
                    see a hint instead of an upload control here. */}
                <Col xs="auto">
                    <img
                        src={(() => {
                            try {
                                const a = JSON.parse(sessionStorage.getItem('authUser') || '{}');
                                return assetUrl(a?.data?.imagePath || a?.imagePath);
                            } catch { return assetUrl(); }
                        })()}
                        alt="profile"
                        className="rounded-circle avatar-lg img-thumbnail"
                        style={{ objectFit: 'cover' }}
                    />
                </Col>
                <Col>
                    <h4 className="mb-0">My Profile</h4>
                    <p className="text-muted small mb-0">
                        {data.fullName} · {data.empId}
                        {data.title && <> · {data.title}</>}
                        {data.dept && <> · Dept {data.dept}</>}
                    </p>
                    <p className="text-muted small mb-0">
                        Need a new photo? Contact HR — photos are updated from the
                        employee management screen.
                    </p>
                </Col>
            </Row>

            {hasPending && (
                <Alert color="warning">
                    <strong>Pending HR review.</strong> You submitted changes on{' '}
                    {new Date(data.pendingRequest.submittedAt).toLocaleString()}.
                    The form below is locked until HR decides. You can withdraw the
                    pending request to make a new submission.
                    <div className="mt-2 small">
                        Proposed changes: {Object.entries(proposed)
                            .filter(([_, v]) => v !== null && v !== undefined)
                            .map(([k]) => <Badge key={k} color="info" className="me-1">{k}</Badge>)}
                    </div>
                    <div className="mt-2">
                        <Button size="sm" color="link" onClick={withdraw}>Withdraw request</Button>
                    </div>
                </Alert>
            )}

            <Card>
                <CardBody>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Address</Label>
                                    <div style={{ position: 'relative' }}>
                                        <Input value={form.address || ''} autoComplete="off"
                                               onChange={(e) => { onChange('address')(e); acSearch(e.target.value); }}
                                               onBlur={() => setTimeout(() => setAcOpen(false), 200)}
                                               disabled={hasPending} />
                                        {acOpen && !hasPending && (
                                            <div style={{
                                                position: 'absolute', zIndex: 1050, left: 0, right: 0, top: '100%',
                                                background: '#fff', border: '0.5px solid #e2e2e2', borderRadius: 8,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden',
                                            }}>
                                                {acItems.map((it, i) => (
                                                    <div key={i}
                                                         onMouseDown={(e) => { e.preventDefault(); acPick(it); }}
                                                         style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}>
                                                        <i className="ri-map-pin-line align-bottom me-1" />
                                                        {it.label}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Address 2</Label>
                                    <Input value={form.address2 || ''} onChange={onChange('address2')} disabled={hasPending} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>City</Label>
                                    <Input value={form.city || ''} onChange={onChange('city')} disabled={hasPending} />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>State</Label>
                                    <Input value={form.state || ''} onChange={onChange('state')} disabled={hasPending} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>ZIP</Label>
                                    <Input value={form.zip || ''} onChange={onChange('zip')}
                                           onBlur={onZipBlur} disabled={hasPending} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Country</Label>
                                    <Input value={form.country || ''} onChange={onChange('country')} disabled={hasPending} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Phone</Label>
                                    <Input value={form.phone || ''} onChange={onChange('phone')} disabled={hasPending} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Emergency contact</Label>
                                    <Input value={form.emgContact || ''} onChange={onChange('emgContact')} disabled={hasPending} />
                                </FormGroup>
                            </Col>
                        </Row>

                        <hr />
                        <p className="text-muted small">
                            Changes don't take effect immediately — they go to HR for review.
                            For things like supervisor, role, title, or hire date, contact HR
                            directly. Those fields are not self-editable.
                        </p>

                        <Button
                            color="primary"
                            onClick={submit}
                            disabled={saving || hasPending}
                        >
                            {saving ? 'Submitting…' : 'Submit for HR review'}
                        </Button>
                    </Form>
                </CardBody>
            </Card>
        </Container>
        </div>
    );
};

export default MyProfile;
